import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    ReportsService,
    CompaniesService,
    IndividualReportInterface,
    CompanyReportInterface,
    IndividualFilterModel,
    IndividualReportModel,
    CompanyFilterModel,
    CompanyReportModel,
} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {Pagination, ReportTableData, ReportTableDataRow} from '../../../shared/components';

import {EnrollTypesEnum, SearchClientFormErrorStatesEnum, TradingGroupClientTypesEnum} from '../enums';

import {
    SearchClientControls,
    SearchClientNameSelector,
    SearchClientTypeSelector,
    SearchClientEmailSelector,
    SearchClientDataTableItems,
    SearchClientControlsInterface,
    SearchClientFieldMinLengthSelector,
    SearchClientEnrollTypeSelector,
    SearchAliasSelector
} from '../../../../assets/descriptors';

export class SearchClientForm {
    private readonly clientType: number;
    private readonly reportsService: ReportsService;
    private readonly companiesService: CompaniesService;

    public form: FormGroup;
    public controls: SearchClientControlsInterface;

    constructor(clientType: number, reportsService: ReportsService, companiesService: CompaniesService) {
        this.clientType = clientType;

        this.reportsService = reportsService;
        this.companiesService = companiesService;
    }

    private getIndividualFilterModel(): IndividualFilterModel {
        return new IndividualFilterModel(
            null,
            null,
            null,
            null,
            null,
            null,
            this.form.value[SearchClientNameSelector],
            null,
            null,
            null,
            this.form.value[SearchClientEmailSelector],
            null,
            null,
            null,
            null,
            null,
            [EnrollTypesEnum.Individual],
            Pagination.defaultPagesChunk,
            5,
            IndividualReportModel.getFirstNameSelector(),
            true,
            null
        );
    }

    private getIndividualsReport(
        filter: IndividualFilterModel
    ): Observable<ApiResponse<ApiError[] | ReportTableDataRow[]>> {
        return this.reportsService.getIndividualsReport(filter).pipe(
            map((response: ApiResponse<ApiError[] | IndividualReportInterface[]>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<IndividualReportInterface[]>(
                        response,
                        SearchClientFormErrorStatesEnum.GetClientFailed
                    );
                }

                let rows = [];

                const isInvalidArrayData = Helper.errorsHelper()
                    .checkForInvalidRespondedArray<IndividualReportInterface>(response);

                if (!isInvalidArrayData) {
                    rows = (response.data as IndividualReportInterface[])
                        .map((item: IndividualReportInterface) => new IndividualReportModel(item))
                        .map((item: IndividualReportModel) => {
                            return new ReportTableDataRow({
                                rowId: item.enrollId,
                                dataItems: this.createDataTableItems(item)
                            });
                        });
                }

                return Helper.createSuccessResponse<ReportTableDataRow[]>(rows);
            })
        );
    }

    private getCompanyFilterModel(): CompanyFilterModel {
        return new CompanyFilterModel(
            this.form.value[SearchClientNameSelector],
            Pagination.defaultPagesChunk,
            5,
            CompanyReportModel.getNameSelector(),
            true
        );
    }

    private createDataTableItems(model: IndividualReportModel | CompanyReportModel): ReportTableData[] {
        return SearchClientDataTableItems().map((item: ReportTableData) => {
            switch (item.colId) {
                case SearchClientTypeSelector:
                    if (this.clientType !== TradingGroupClientTypesEnum.Individual) {
                        item.value = Helper.textsHelper()
                            .getTradingGroupClientType(TradingGroupClientTypesEnum.Company);
                    } else {
                        item.value = Helper.textsHelper()
                            .getTradingGroupClientType(TradingGroupClientTypesEnum.Individual);
                    }
                    break;
                case SearchClientNameSelector:
                    item.value = (model instanceof IndividualReportModel) ? (model.firstName + ' ' + model.lastName) :
                        model.name;
                    break;
                case SearchClientEnrollTypeSelector:
                    item.value = (model instanceof IndividualReportModel) ? model.enrollType : EnrollTypesEnum.Corporate;
                    item.hidden = true;
                    break;
                case SearchClientEmailSelector:
                    item.value = (model instanceof IndividualReportModel) ? model.email : '';
                    break;
                case SearchAliasSelector:
                    item.value = model.alias;
                    break;
            }

            return item;
        });
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = SearchClientControls();

        if (this.clientType === TradingGroupClientTypesEnum.Company) {
            this.controls.clientEmail.disabled = true;
        }

        Object.keys(this.controls).forEach((key: string) => {
            const control = this.controls[key];

            if (control.name === SearchClientTypeSelector) {
                control.value = this.clientType;
            }
            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messageFunction(control.placeholder, SearchClientFieldMinLengthSelector)[errorKey];
                    });
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | ReportTableDataRow[]>> {
        if (this.clientType !== TradingGroupClientTypesEnum.Company) {
            const individualFilter = this.getIndividualFilterModel();

            return this.getIndividualsReport(individualFilter);
        } else {
            const companyFilterModel = this.getCompanyFilterModel();

            return this.reportsService.getCompaniesReport(companyFilterModel)
                .pipe(
                    map((response: ApiResponse<ApiError[] | CompanyReportInterface[]>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<CompanyReportInterface[]>(
                                response,
                                SearchClientFormErrorStatesEnum.GetClientFailed
                            );
                        }

                        let rows = [];

                        const isInvalidArrayData = Helper.errorsHelper()
                            .checkForInvalidRespondedArray<CompanyReportInterface>(response);

                        if (!isInvalidArrayData) {
                            rows = (response.data as CompanyReportInterface[])
                                .map((item: CompanyReportInterface) => new CompanyReportModel(item))
                                .map((item: CompanyReportModel) => {
                                    return new ReportTableDataRow({
                                        rowId: item.id,
                                        dataItems: this.createDataTableItems(item)
                                    });
                                });
                        }

                        return Helper.createSuccessResponse<ReportTableDataRow[]>(rows);
                    })
                );
        }
    }
}
